import HideIcon from "@mui/icons-material/VisibilityOffOutlined";
import ShowIcon from "@mui/icons-material/VisibilityOutlined";
import {
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * A password as it is typed: hidden, until its button shows it. With
 * `shownForMs` it is hidden again that long after it was shown or last
 * changed, so that it does not stay readable on a screen nobody types on.
 */
export const PasswordField = ({
  label,
  value,
  onChange,
  autoComplete,
  error,
  hint,
  shownForMs,
  size = "medium",
  ...textField
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
  /** What is wrong with the entry; takes the place of the hint. */
  error?: string;
  hint?: ReactNode;
  /**
   * How many milliseconds the password stays shown; without it, until its
   * button hides it.
   */
  shownForMs?: number;
  size?: "small" | "medium";
} & Pick<
  TextFieldProps,
  "id" | "name" | "required" | "margin" | "autoFocus" | "disabled" | "onBlur"
>) => {
  const { t } = useTranslation(undefined, { keyPrefix: "passwordField" });
  const [shown, setShown] = useState(false);

  // the time counts anew with every change, so that the password does not
  // turn back into dots while it is being typed
  useEffect(() => {
    if (!shown || shownForMs === undefined) return undefined;
    const hiding = window.setTimeout(() => setShown(false), shownForMs);
    return () => window.clearTimeout(hiding);
  }, [shown, shownForMs, value]);

  return (
    <TextField
      {...textField}
      label={label}
      type={shown ? "text" : "password"}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={error !== undefined}
      helperText={error ?? hint}
      size={size}
      fullWidth
      // Edge draws a button of its own that shows the password, without a
      // limit of time
      sx={{ "& input::-ms-reveal": { display: "none" } }}
      slotProps={{
        htmlInput: { autoComplete },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={t(shown ? "hide" : "show", { field: label })}
                onClick={() => setShown(!shown)}
                // the cursor stays in the field, for typing to go on
                onMouseDown={(event) => event.preventDefault()}
                size={size}
                edge="end"
              >
                {shown ? (
                  <HideIcon fontSize={size} />
                ) : (
                  <ShowIcon fontSize={size} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
